import React, { useState } from 'react';
import { BaseConfigProps, commonStyles } from '../shared/ConfigFormHelpers';
import CollapsibleSection from '../shared/CollapsibleSection';

const PDFGeneratorConfig: React.FC<BaseConfigProps> = ({ node, updateConfig }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic', 'template']));
  
  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const formats = [
    { value: 'A4', label: 'A4', desc: '210 × 297 mm', icon: '📄' },
    { value: 'Letter', label: 'Letter', desc: '8.5 × 11 inch', icon: '📄' },
    { value: 'A3', label: 'A3', desc: '297 × 420 mm', icon: '📋' },
    { value: 'Legal', label: 'Legal', desc: '8.5 × 14 inch', icon: '📋' },
  ];

  const orientations = [
    { value: 'portrait', label: 'Álló', icon: '📱' },
    { value: 'landscape', label: 'Fekvő', icon: '📺' },
  ];

  return (
    <div className="space-y-3">
      {/* Alapbeállítások */}
      <CollapsibleSection
        id="basic"
        title="PDF Beállítások"
        icon="📕"
        color="red"
        isExpanded={expandedSections.has('basic')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              Papír formátum
            </label>
            <div className="grid grid-cols-2 gap-2">
              {formats.map((format) => (
                <button
                  key={format.value}
                  onClick={() => updateConfig('format', format.value)}
                  className={`flex items-start p-2 rounded-md border text-sm transition-all ${
                    (node.data.config.format || 'A4') === format.value
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="text-lg mr-2">{format.icon}</span>
                  <div className="flex-1 text-left">
                    <div className="font-medium">{format.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{format.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={commonStyles.label}>
              Tájolás
            </label>
            <div className="grid grid-cols-2 gap-2">
              {orientations.map((orientation) => (
                <button
                  key={orientation.value}
                  onClick={() => updateConfig('orientation', orientation.value)}
                  className={`flex items-center justify-center p-3 rounded-md border text-sm transition-all ${
                    (node.data.config.orientation || 'portrait') === orientation.value
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                  }`}
                >
                  <span className="text-2xl mr-2">{orientation.icon}</span>
                  <span className="font-medium">{orientation.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={commonStyles.label}>
              Fájl név
            </label>
            <input
              type="text"
              value={node.data.config.filename || ''}
              onChange={(e) => updateConfig('filename', e.target.value)}
              className={commonStyles.input}
              placeholder="document.pdf vagy {{filename}}.pdf"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Opcionális - ha üres, automatikus név generálódik
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* Template */}
      <CollapsibleSection
        id="template"
        title="HTML Template"
        icon="📋"
        color="blue"
        isExpanded={expandedSections.has('template')}
        onToggle={toggleSection}
      >
        <div className="space-y-3">
          <div>
            <label className={commonStyles.label}>
              HTML tartalom
            </label>
            <textarea
              value={node.data.config.template || ''}
              onChange={(e) => updateConfig('template', e.target.value)}
              className={`${commonStyles.textarea} font-mono text-xs`}
              rows={8}
              placeholder={`<html>
<head>
  <style>
    body { font-family: Arial; }
    h1 { color: #333; }
  </style>
</head>
<body>
  <h1>{{title}}</h1>
  <p>{{content}}</p>
</body>
</html>`}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Használj HTML-t és változókat
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
            <div className="text-xs text-blue-700 dark:text-blue-300">
              <strong>💡 Tipp:</strong> Használd a <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{"{{változó}}"}</code> szintaxist az adatok beillesztéséhez.
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Elérhető Változók */}
      <CollapsibleSection
        id="variables"
        title="Kimenet Változók"
        icon="🔗"
        color="purple"
        isExpanded={expandedSections.has('variables')}
        onToggle={toggleSection}
      >
        <div className="space-y-2">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
            A PDF generálás után ezek a változók lesznek elérhetők:
          </div>
          
          <div className="grid grid-cols-1 gap-2">
            {[
              { var: '{{pdf_url}}', desc: 'PDF fájl URL', example: 'https://storage.../doc.pdf' },
              { var: '{{pdf_size}}', desc: 'PDF méret (byte)', example: '52480' },
              { var: '{{pages}}', desc: 'Oldalak száma', example: '3' },
              { var: '{{filename}}', desc: 'Generált fájlnév', example: 'document_2024.pdf' }
            ].map((item) => (
              <div key={item.var} className="bg-gray-50 dark:bg-gray-800 rounded-md p-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <code className="text-xs font-mono text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-2 py-1 rounded">
                    {item.var}
                  </code>
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                    {item.example}
                  </span>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {item.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Példák */}
      <CollapsibleSection
        id="examples"
        title="Használati Példák"
        icon="💡"
        color="green"
        isExpanded={expandedSections.has('examples')}
        onToggle={toggleSection}
      >
        <div className="space-y-2 text-xs text-gray-600 dark:text-gray-400">
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📄 Számla generálás:</strong>
            <div className="mt-1 font-mono text-[10px]">
              CRM adatok → PDF generálás → Email melléklet
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📊 Report készítés:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Adatbázis → Szöveg formázó → PDF → Tárolás
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700">
            <strong>📋 Szerződés:</strong>
            <div className="mt-1 font-mono text-[10px]">
              Űrlap → PDF generálás → FlintSign aláírás
            </div>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

export default PDFGeneratorConfig;
